import { useMemo, useState, useEffect } from "react";
import {
  Card,
  Empty,
  Spin,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Select,
  Button,
  Tooltip,
  Badge,
  Alert,
  Switch,
} from "antd";
import {
  EnvironmentOutlined,
  ShopOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { CrossMerchantAnalytics, CrossMerchantConsumer } from "../../../interfaces/crossMerchantAnalytics.interface";
import InteractiveMap from "./InteractiveMap";
import styles from "./CrossMerchantMapView.module.css";

const { Title, Text } = Typography;
const { Option } = Select;

interface CrossMerchantMapViewProps {
  data: CrossMerchantAnalytics | null;
  selectedConsumer: string | null;
  onConsumerSelect: (par: string | null) => void;
  loading: boolean;
}

export default function CrossMerchantMapView({
  data,
  selectedConsumer,
  onConsumerSelect,
  loading,
}: CrossMerchantMapViewProps) {
  const [selectedConsumerFilter, setSelectedConsumerFilter] = useState<string | null>(
    selectedConsumer
  );
  const [showJourneyPath, setShowJourneyPath] = useState<boolean>(false);

  // Update filter when selectedConsumer prop changes (from View Map button)
  // Also enable Show Journey Path automatically
  useEffect(() => {
    if (selectedConsumer) {
      setSelectedConsumerFilter(selectedConsumer);
      // Auto-enable journey path when coming from "View Map" button
      setShowJourneyPath(true);
    }
  }, [selectedConsumer]);

  // Get all merchants with coordinates
  const merchantsWithCoordinates = useMemo(() => {
    if (!data?.consumers) return [];
    const allMerchants: Array<{
      consumer: CrossMerchantConsumer;
      merchant: CrossMerchantConsumer["merchants"][0];
    }> = [];
    data.consumers.forEach((consumer) => {
      consumer.merchants?.forEach((merchant) => {
        if (merchant.coordinates) {
          allMerchants.push({ consumer, merchant });
        }
      });
    });
    return allMerchants;
  }, [data]);

  // Filter by selected consumer
  const filteredMerchants = useMemo(() => {
    if (!selectedConsumerFilter) return merchantsWithCoordinates;
    return merchantsWithCoordinates.filter(
      (item) => item.consumer.paymentAccountReference === selectedConsumerFilter
    );
  }, [merchantsWithCoordinates, selectedConsumerFilter]);

  // Get unique consumers for filter dropdown
  const uniqueConsumers = useMemo(() => {
    if (!data?.consumers) return [];
    return data.consumers.map((consumer) => ({
      par: consumer.paymentAccountReference,
      merchantCount: consumer.merchantCount,
      totalAmount: consumer.totalAmount,
    }));
  }, [data]);

  const formatCurrency = (amount: number) => {
    if (typeof amount !== "number" || isNaN(amount)) {
      return "€0.00";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (!date.isValid()) return "Invalid Date";
    return date.format("MMM DD, YYYY HH:mm");
  };

  // Calculate center of map from merchants
  const mapCenter = useMemo(() => {
    if (filteredMerchants.length === 0) {
      return { lat: 52.3676, lng: 4.9041 }; // Default to Amsterdam
    }
    const avgLat =
      filteredMerchants.reduce(
        (sum, item) => sum + item.merchant.coordinates!.lat,
        0
      ) / filteredMerchants.length;
    const avgLng =
      filteredMerchants.reduce(
        (sum, item) => sum + item.merchant.coordinates!.lng,
        0
      ) / filteredMerchants.length;
    return { lat: avgLat, lng: avgLng };
  }, [filteredMerchants]);

  if (loading) {
    return (
      <div className={styles.map_container}>
        <Spin size="large" tip="Loading map data..." />
      </div>
    );
  }

  if (!data || merchantsWithCoordinates.length === 0) {
    return (
      <Card className={styles.map_card}>
        <Empty
          description={
            <div>
              <Text type="secondary">
                No merchants with geographic coordinates found.
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Coordinates are being geocoded in the background. Please refresh
                later.
              </Text>
            </div>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <div className={styles.map_view_container}>
      <Card
        title={
          <Space>
            <GlobalOutlined />
            <span>Geographic Consumer Journey Map</span>
            <Badge
              count={filteredMerchants.length}
              style={{ backgroundColor: "#52c41a" }}
            />
          </Space>
        }
        extra={
          <Space className={styles.map_controls_desktop}>
            <Select
              placeholder="Filter by Consumer"
              allowClear
              style={{ width: 250 }}
              value={selectedConsumerFilter}
              onChange={(value) => {
                setSelectedConsumerFilter(value);
                onConsumerSelect(value);
              }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                if (!option?.children) return false;
                return String(option.children).toLowerCase().includes(input.toLowerCase());
              }}
            >
              {uniqueConsumers.map((consumer) => (
                <Option key={consumer.par} value={consumer.par}>
                  {consumer.par.substring(0, 25)}... ({consumer.merchantCount}{" "}
                  merchants)
                </Option>
              ))}
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSelectedConsumerFilter(null);
                onConsumerSelect(null);
              }}
            >
              Clear Filter
            </Button>
          </Space>
        }
        className={styles.map_card}
      >
        {/* Mobile controls - shown only on mobile */}
        <div className={styles.map_controls_mobile}>
          <Select
            placeholder="Filter by Consumer"
            allowClear
            value={selectedConsumerFilter}
            onChange={(value) => {
              setSelectedConsumerFilter(value);
              onConsumerSelect(value);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              if (!option?.children) return false;
              return String(option.children).toLowerCase().includes(input.toLowerCase());
            }}
          >
            {uniqueConsumers.map((consumer) => (
              <Option key={consumer.par} value={consumer.par}>
                {consumer.par.substring(0, 25)}... ({consumer.merchantCount}{" "}
                merchants)
              </Option>
            ))}
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSelectedConsumerFilter(null);
              onConsumerSelect(null);
            }}
            block
          >
            Clear Filter
          </Button>
        </div>

        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            message="Geographic Consumer Journey Map"
            description={
              <div>
                <Text>
                  {selectedConsumerFilter
                    ? `Showing ${filteredMerchants.length} merchant locations for selected consumer.`
                    : `Showing ${filteredMerchants.length} merchant locations across all consumers.`}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Map center: {mapCenter.lat.toFixed(6)}, {mapCenter.lng.toFixed(6)}
                </Text>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <Space>
              <Text strong>Show Journey Path:</Text>
              <Switch
                checked={showJourneyPath}
                onChange={setShowJourneyPath}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            </Space>
            {selectedConsumerFilter && (
              <Button
                type="link"
                onClick={() => {
                  setSelectedConsumerFilter(null);
                  onConsumerSelect(null);
                }}
              >
                Clear Consumer Filter
              </Button>
            )}
          </div>

          {/* Interactive Map Component */}
          <InteractiveMap
            merchants={filteredMerchants}
            selectedConsumer={selectedConsumerFilter}
            showJourneyPath={showJourneyPath}
            onMarkerClick={(merchant) => {
              onConsumerSelect(merchant.consumer.paymentAccountReference);
            }}
          />
        </Space>
      </Card>
    </div>
  );
}

